import React from 'react';
import { useAbility } from '@casl/react';
import { find } from 'lodash';

import { getOrderTotal, isAsphaltShingle } from '@/components/modals/SubmitOrder/constants';
import { useOrderContext } from '@/components/modals/SubmitOrder/helpers';
import { ProductLineName } from '@/components/modals/SubmitOrder/types';
import { CalculationMethod, ProposalWithBom } from '@/controllers/Activity';
import { useBomTotal } from '@/hooks';
import { AbilityContext } from '@/hooks/ability/Can';
import { formatPriceWithCurrency } from '@/utils/format-price';

import { OrderBlockWrapper } from '../../../Common/OrderBlockWrapper';

import styles from './OrderDetails.module.scss';

export type Props = {
  productLineName: ProductLineName;
  proposal: ProposalWithBom | null;
  goToOrderDetails: () => void;
};

export const OrderDetails = (props: Props) => {
  const state = useOrderContext();
  const ability = useAbility(AbilityContext);
  const shingleItem = find(state.visibleOrderItems, oi => isAsphaltShingle(oi));

  const shingleColor = shingleItem?.priceBookEntry?.asphaltShingleColor ?? null;
  const { productLineName, goToOrderDetails, proposal } = props;
  const enableSkuFlow =
    ability.can('view', 'SKU_PRICING') &&
    !ability.can('view', 'Bulk Fulfillment Program') &&
    proposal?.priceCalculationMethod === CalculationMethod.SKU;

  const bomTotal = useBomTotal(state, proposal);

  if (enableSkuFlow) {
    return (
      <OrderBlockWrapper className={styles.order_details_wrapper}>
        <div className={styles.details_header}>
          <span>Order Details</span>
          <a className={styles.edit_order} onClick={goToOrderDetails}>
            Edit Order
          </a>
        </div>
        <div className={styles.details_content}>
          <div className={styles.product_name_wrapper}>
            <span>Total Costs</span>
            <div className={styles.product_content}>
              {state.selectedServices && (
                <span className={styles.product_services}> | + Services</span>
              )}
            </div>
          </div>
          {!state.isPriceHidden && (
            <div className={styles.product_total_wrapper}>
              <span className={styles.total}>{formatPriceWithCurrency(bomTotal)}</span>
            </div>
          )}
        </div>
      </OrderBlockWrapper>
    );
  }

  return (
    <OrderBlockWrapper className={styles.order_details_wrapper}>
      <div className={styles.details_header}>
        <span>Order Details</span>
        <a className={styles.edit_order} onClick={goToOrderDetails}>
          Edit Order
        </a>
      </div>

      <div className={styles.details_content}>
        <div className={styles.product_name_wrapper}>
          <span>{productLineName}</span>
          <div className={styles.product_content}>
            {shingleColor && <span className={styles.shingle_color}>{shingleColor}</span>}
            {state.selectedServices && (
              <span className={styles.product_services}> | + Services</span>
            )}
          </div>
        </div>
        {!state.isPriceHidden && (
          <div className={styles.product_total_wrapper}>
            <span>Total</span>
            <span className={styles.total}>
              {formatPriceWithCurrency(getOrderTotal(state, productLineName, proposal))}
            </span>
          </div>
        )}
      </div>
      {!state.isPriceHidden && (
        <div className={styles.product_taxes_wrapper}>
          <span className={styles.product_taxes}>(Taxes calculated at shipping)</span>
        </div>
      )}
    </OrderBlockWrapper>
  );
};
